import { useState, useMemo, useCallback } from 'react';
import { CalendarIcon, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { useTranslations } from '@/i18n';
import { format, parseISO, isValid } from 'date-fns';

interface DateFieldOption {
  id: string;
  label: string;
  placeholder?: string;
}

export interface FilterOption {
  id: string;
  label: string;
  value: string;
}

export interface FilterSection {
  id: string;
  title: string;
  options?: FilterOption[];
  multiSelect?: boolean;
  type?: 'options' | 'dateRange';
  fields?: DateFieldOption[];
}

export interface FilterDropdownProps {
  sections: FilterSection[];
  onFiltersChange?: (filters: Record<string, string[]>) => void;
  className?: string;
}

function formatDisplayDate(value?: string) {
  if (!value) {
    return undefined;
  }

  const parsed = parseISO(value);
  if (!isValid(parsed)) {
    return undefined;
  }

  return format(parsed, 'dd/MM/yyyy');
}

interface DateFilterInputProps {
  label: string;
  value?: string;
  placeholder: string;
  clearLabel: string;
  onChange: (value: string | undefined) => void;
}

function DateFilterInput({
  label,
  value,
  placeholder,
  clearLabel,
  onChange,
}: DateFilterInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const displayValue = value ? (formatDisplayDate(value) ?? value) : placeholder;
  const parsedDate = value ? parseISO(value) : undefined;
  const hasValidDate = parsedDate && isValid(parsedDate);

  const handleSelect = useCallback(
    (date: Date | undefined) => {
      if (!date) {
        return;
      }
      onChange(format(date, 'yyyy-MM-dd'));
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[0.9375rem] leading-[1.5rem] text-[#292929]">{label}</span>
      <div className="flex items-center gap-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex h-10 w-full items-center justify-between rounded-[0.75rem] border border-[#ebebeb] px-3 text-left text-[0.9375rem] leading-[1.5rem] text-[#292929] transition-colors hover:border-[#d6d6d6]"
            >
              <span className={value ? 'text-[#292929]' : 'text-[#9c9c9c]'}>{displayValue}</span>
              <CalendarIcon className="h-4 w-4 text-[#9c9c9c]" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-auto rounded-[1rem] border border-[#ebebeb] bg-white p-0 shadow-[0px_12px_32px_-8px_rgba(31,31,31,0.1)]"
          >
            <Calendar
              mode="single"
              selected={hasValidDate ? parsedDate : undefined}
              onSelect={handleSelect}
              initialFocus
            />
            <div className="flex justify-end border-t border-[#f1f1f1] px-3 py-2">
              <button
                type="button"
                onClick={handleClear}
                className="rounded-[0.625rem] px-2 py-1 text-[0.8125rem] font-medium text-[#006068] hover:bg-[#f1f1f1]"
              >
                {clearLabel}
              </button>
            </div>
          </PopoverContent>
        </Popover>
        {value && (
          <button
            type="button"
            aria-label={clearLabel}
            onClick={handleClear}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#ebebeb] text-[#9c9c9c] transition-colors hover:border-[#d6d6d6] hover:text-[#292929]"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function FilterDropdown({
  sections,
  onFiltersChange,
  className = '',
}: FilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [dateValues, setDateValues] = useState<Record<string, string>>({});
  const t = useTranslations('common');

  const emitFilters = useCallback(
    (filters: Record<string, string[]>, dates: Record<string, string>) => {
      const combined: Record<string, string[]> = {};

      Object.entries(filters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          combined[key] = values;
        }
      });

      Object.entries(dates).forEach(([key, value]) => {
        if (value) {
          combined[key] = [value];
        }
      });

      onFiltersChange?.(combined);
    },
    [onFiltersChange]
  );

  // Calculate total active filters
  const activeFiltersCount = useMemo(() => {
    const optionCount = Object.values(selectedFilters).reduce(
      (total, filters) => total + filters.length,
      0
    );
    const dateCount = Object.values(dateValues).filter(Boolean).length;
    return optionCount + dateCount;
  }, [selectedFilters, dateValues]);

  const handleSectionToggle = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const handleFilterToggle = (sectionId: string, optionId: string, multiSelect = false) => {
    setSelectedFilters((prev) => {
      const current = prev[sectionId] || [];
      let updated: string[];

      if (multiSelect) {
        // Multi-select: toggle the option
        updated = current.includes(optionId)
          ? current.filter((id) => id !== optionId)
          : [...current, optionId];
      } else {
        // Single-select: replace or clear
        updated = current.includes(optionId) ? [] : [optionId];
      }

      const nextFilters = { ...prev };
      if (updated.length > 0) {
        nextFilters[sectionId] = updated;
      } else {
        delete nextFilters[sectionId];
      }

      emitFilters(nextFilters, dateValues);
      return nextFilters;
    });
  };

  const handleDateValueChange = (fieldId: string, value: string | undefined) => {
    setDateValues((prev) => {
      const next = { ...prev };
      if (value) {
        next[fieldId] = value;
      } else {
        delete next[fieldId];
      }

      emitFilters(selectedFilters, next);
      return next;
    });
  };

  const handleClearAll = () => {
    setSelectedFilters({});
    setDateValues({});
    emitFilters({}, {});
  };

  const handleApply = () => {
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="unstyled"
          size="none"
          className={`flex h-[2.5rem] items-center justify-center gap-2 rounded-xl border-1 px-4 py-2.5 pr-2.5 transition-colors ${
            isOpen || activeFiltersCount > 0
              ? 'border-[#006068] bg-[rgba(0,173,204,0.02)] text-[#2f717a]'
              : 'border-[#ebebeb] text-black hover:bg-gray-50'
          } ${className}`}
        >
          <div className="flex h-4 w-4 shrink-0 items-center justify-center">
            <img
              src="/assets/icons/filter.svg"
              alt="Filter"
              className="h-full w-full object-contain transition-all"
              style={{
                filter:
                  isOpen || activeFiltersCount > 0
                    ? 'invert(41%) sepia(45%) saturate(1086%) hue-rotate(148deg) brightness(96%) contrast(89%)'
                    : 'none',
              }}
            />
          </div>
          <span className="shrink-0 text-center text-[14px] leading-[20px] font-medium whitespace-nowrap">
            {t('filter')}
          </span>
          {activeFiltersCount > 0 && (
            <div className="flex h-[20px] min-w-[20px] shrink-0 items-center justify-center rounded-full bg-[#2f717a] px-[6px] py-[2px] text-[12px] text-white">
              {activeFiltersCount}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-screen max-w-[100vw] h-[var(--radix-dropdown-menu-content-available-height)] overflow-hidden rounded-none border border-[#eeeff0] bg-white p-0 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] md:h-auto md:w-[24.125rem] md:overflow-y-auto md:rounded-[0.75rem]"
      >
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between rounded-t-[1.5rem] border-b border-[#f1f1f1] bg-white px-[1rem] py-[0.75rem]">
            <h3 className="text-[1rem] leading-[1.5rem] font-medium text-black">{t('filter')}</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-full bg-[#f1f1f1] p-[0.375rem] hover:bg-[#e8e8e8]"
            >
              <X className="h-[1.25rem] w-[1.25rem] text-black" />
            </button>
          </div>

          {/* Filter Sections */}
          <div className="flex min-h-0 flex-1 flex-col gap-[1.25rem] overflow-y-auto bg-white p-[1rem]">
            <div className="flex flex-col gap-[1rem]">
              {sections.map((section) => {
                const isExpanded = expandedSections[section.id];
                const optionSelections = selectedFilters[section.id] || [];
                const dateSelections =
                  section.type === 'dateRange' && section.fields
                    ? section.fields.reduce(
                        (count, field) => (dateValues[field.id] ? count + 1 : count),
                        0
                      )
                    : 0;
                const selectedCount =
                  section.type === 'dateRange' ? dateSelections : optionSelections.length;
                const hasSelectedFilters = selectedCount > 0;

                return (
                  <div key={section.id} className="flex flex-col gap-[0.75rem]">
                    {/* Section Header */}
                    <div className="flex flex-col gap-[0.625rem] rounded-2xl">
                      <button
                        onClick={() => handleSectionToggle(section.id)}
                        className="relative flex w-full items-center justify-between rounded-2xl border border-[#ebebeb] p-[0.75rem] hover:bg-gray-50"
                      >
                        <span className="text-left text-[1rem] leading-[1.5rem] font-medium text-black">
                          {section.title}
                          {hasSelectedFilters && ` (${selectedCount})`}
                        </span>
                        <div className="flex items-center justify-center">
                          {isExpanded ? (
                            <ChevronUp className="h-[1.25rem] w-[1.25rem] text-black" />
                          ) : (
                            <ChevronDown className="h-[1.25rem] w-[1.25rem] text-black" />
                          )}
                        </div>
                      </button>
                    </div>

                    {/* Section Options */}
                    {isExpanded && (
                      <div className="flex flex-col gap-3">
                        {section.type === 'dateRange' && section.fields
                          ? section.fields.map((field) => (
                              <DateFilterInput
                                key={field.id}
                                label={field.label}
                                value={dateValues[field.id]}
                                placeholder={field.placeholder ?? ''}
                                clearLabel={t('clear')}
                                onChange={(nextValue) => handleDateValueChange(field.id, nextValue)}
                              />
                            ))
                          : section.options?.map((option) => {
                              const isSelected = optionSelections.includes(option.id);

                              return (
                                <div
                                  key={option.id}
                                  className="flex flex-col items-start justify-center gap-2 overflow-clip rounded-2xl"
                                >
                                  <label
                                    className="flex w-full cursor-pointer items-center justify-start gap-3 rounded p-[0.625rem] transition-colors duration-150 hover:bg-gray-50"
                                    onClick={() =>
                                      handleFilterToggle(section.id, option.id, section.multiSelect)
                                    }
                                  >
                                    <div className="flex min-h-px min-w-px grow basis-0 items-center justify-start gap-3">
                                      <div className="flex min-h-px min-w-px grow basis-0 flex-col items-start justify-center gap-1">
                                        <div className="flex w-full flex-col items-start justify-start">
                                          <span className="w-full overflow-hidden text-[0.9375rem] leading-[1.5rem] text-nowrap overflow-ellipsis text-black">
                                            {option.label}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex flex-col items-start justify-start gap-2">
                                      <div className="flex items-start justify-start">
                                        <div className="flex items-center justify-center gap-2 rounded">
                                          <div
                                            className={`pointer-events-none relative inline-flex h-5 w-5 items-center justify-center rounded-[0.208rem] border-2 transition-all duration-150 ease-in-out ${
                                              isSelected
                                                ? 'border-black bg-black'
                                                : 'border-[#bbbbbb] bg-white'
                                            }`}
                                          >
                                            <svg
                                              className={`h-3 w-3 text-white transition-opacity duration-150 ease-in-out ${
                                                isSelected ? 'opacity-100' : 'opacity-0'
                                              }`}
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </label>
                                </div>
                              );
                            })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex shrink-0 items-center justify-end gap-[0.25rem] rounded-b-[1.5rem] border-t border-[#f1f1f1] px-[1.5rem] py-[1rem]">
            <button
              onClick={handleClearAll}
              className="rounded-[0.75rem] px-[0.875rem] py-[0.625rem] hover:bg-gray-50"
            >
              <span className="text-[0.875rem] leading-[1.25rem] font-medium text-black">
                {t('clear')}
              </span>
            </button>
            <button
              onClick={handleApply}
              className="rounded-[1.5rem] bg-[#242424] px-[0.875rem] py-[0.625rem] hover:bg-[#333]"
            >
              <span className="text-[0.875rem] leading-[1.25rem] font-medium text-white">
                {t('save')}
              </span>
            </button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
